import type { Editor, TiptapEditorHTMLElement } from '@tiptap/core';

/**
 * THE @ AND / MENUS AS A HOST SEES THEM (#981). `CustomSuggestion` draws both menus in a popup
 * on `document.body`, outside the card, popover or panel that holds the editor. A host with
 * its own click or key rules cannot tell from the DOM that the menu belongs to its editor:
 *
 *  · CLICKS: the popup carries `SUGGESTION_MENU_ATTRIBUTE`, so a host that closes on a click
 *    outside itself can treat a click on the menu as a click inside.
 *  · ENTER: a host that handles Enter itself must call `suggestionMenuTakesEnter` first. The
 *    mark popover is one: it commits its note on Enter. While the menu shows a row, Enter
 *    picks that row.
 *  · ESC (#897): ProseMirror prevents the default of EVERY Esc in its editor, so a host
 *    cannot read `defaultPrevented` as "the menu took it". The menu marks the Esc that
 *    closes it instead; a host with its own Esc rule skips a press `suggestionMenuTookEsc`.
 *
 * This module has no runtime dependencies on purpose. Hosts that lazy-load the editor import
 * it statically, so it must not pull the editor in with it.
 */
export const SUGGESTION_MENU_ATTRIBUTE = 'data-suggestion-menu';
export const SUGGESTION_MENU_SELECTOR = `[${SUGGESTION_MENU_ATTRIBUTE}]`;

/** Per editor, its menus that show at least one row. The @ and / menus are separate. */
const menusWithRows = new WeakMap<Editor, Set<symbol>>();

/** `CustomSuggestion` reports a menu each time it opens, re-filters or closes. */
export const reportSuggestionMenuRows = (editor: Editor, menuKey: symbol, hasRows: boolean) => {
  const menus = menusWithRows.get(editor) ?? new Set<symbol>();
  if (hasRows) menus.add(menuKey);
  else menus.delete(menuKey);
  menusWithRows.set(editor, menus);
};

/** Whether Enter in this editable picks a row from its open @ or / menu. */
export const suggestionMenuTakesEnter = (editable: Element): boolean => {
  const { editor } = editable as TiptapEditorHTMLElement;
  return Boolean(editor && menusWithRows.get(editor)?.size);
};

/** The Esc keydowns that closed a menu the user could see. */
const escsThatClosedAMenu = new WeakSet<Event>();

/** `CustomSuggestion` marks an Esc while its menu shows, before the menu closes on it. */
export const markSuggestionMenuEsc = (event: Event) => {
  escsThatClosedAMenu.add(event);
};

/** Whether this Esc closed an open @ or / menu, so a host must not act on it as well. */
export const suggestionMenuTookEsc = (event: Event): boolean => escsThatClosedAMenu.has(event);
